import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  projectService,
  milestoneService,
  expenseService,
  teamMemberService,
  timeTrackingService,
} from '@/services/project';
import type {
  ProjectInsert,
  ProjectUpdate,
  ProjectSearchOptions,
  ProjectMilestoneInsert,
  ProjectMilestoneUpdate,
  ProjectExpenseInsert,
  ProjectExpenseUpdate,
  ProjectTeamMemberInsert,
  ProjectTeamMemberUpdate,
  ProjectTimeEntryInsert,
  ProjectTimeEntryUpdate,
} from '@/types/project.types';

// Project hooks
export const useProjects = (options?: ProjectSearchOptions) => {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['projects', options],
    queryFn: () => projectService.fetchProjects(options),
    enabled: !!session,
  });
};

export const useProject = (id: string) => {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => projectService.fetchProjectById(id),
    enabled: !!session && !!id,
  });
};

export const useProjectMetrics = (organizationId?: string) => {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['project-metrics', organizationId],
    queryFn: () => projectService.getProjectMetrics(organizationId),
    enabled: !!session,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (project: ProjectInsert) => projectService.createProject(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-metrics'] });
      toast.success('Project created successfully');
    },
    onError: (error: any) => {
      console.error('Project creation error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        statusCode: error.statusCode,
        stack: error.stack
      });
      
      // More specific error messages
      let errorMessage = 'Failed to create project';
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      if (error.hint) {
        errorMessage += ` (${error.hint})`;
      }
      
      toast.error(errorMessage);
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: ProjectUpdate }) =>
      projectService.updateProject(id, update),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] });
      toast.success('Project updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update project');
      },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-metrics'] });
      toast.success('Project deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete project');
      },
  });
};

export const useGenerateProjectCode = () => {
  return useMutation({
    mutationFn: (organizationId: string) => projectService.generateProjectCode(organizationId),
  });
};

// Enhanced project form hooks
export const useOrganizationsWithDetails = () => {
  return useQuery({
    queryKey: ['organizations-with-details'],
    queryFn: () => projectService.getOrganizationsWithDetails(),
  });
};

export const useOrganizationContacts = (organizationId: string) => {
  return useQuery({
    queryKey: ['organization-contacts', organizationId],
    queryFn: () => projectService.getOrganizationContacts(organizationId),
    enabled: !!organizationId,
  });
};

export const useProjectManagers = () => {
  return useQuery({
    queryKey: ['project-managers'],
    queryFn: () => projectService.getProjectManagers(),
  });
};

// Milestone hooks
export const useProjectMilestones = (projectId: string) => {
  return useQuery({
    queryKey: ['project-milestones', projectId],
    queryFn: () => milestoneService.fetchProjectMilestones(projectId),
    enabled: !!projectId,
  });
};

export const useMilestoneStats = (projectId: string) => {
  return useQuery({
    queryKey: ['milestone-stats', projectId],
    queryFn: () => milestoneService.getMilestoneStats(projectId),
    enabled: !!projectId,
  });
};

export const useCreateMilestone = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (milestone: ProjectMilestoneInsert) => milestoneService.createMilestone(milestone),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-milestones', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['milestone-stats', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.project_id] });
      toast.success('Milestone created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create milestone');
      },
  });
};

export const useUpdateMilestone = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: ProjectMilestoneUpdate }) =>
      milestoneService.updateMilestone(id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-milestones', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['milestone-stats', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.project_id] });
      toast.success('Milestone updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update milestone');
      },
  });
};

export const useDeleteMilestone = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => milestoneService.deleteMilestone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-milestones'] });
      queryClient.invalidateQueries({ queryKey: ['milestone-stats'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Milestone deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete milestone');
      },
  });
};

// Expense hooks
export const useProjectExpenses = (projectId: string) => {
  return useQuery({
    queryKey: ['project-expenses', projectId],
    queryFn: () => expenseService.fetchProjectExpenses(projectId),
    enabled: !!projectId,
  });
};

export const useExpenseStats = (projectId: string) => {
  return useQuery({
    queryKey: ['expense-stats', projectId],
    queryFn: () => expenseService.getExpenseStats(projectId),
    enabled: !!projectId,
  });
};

export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (expense: ProjectExpenseInsert) => expenseService.createExpense(expense),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-expenses', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['expense-stats', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.project_id] });
      toast.success('Expense submitted successfully');
    },
    onError: (error) => {
      toast.error('Failed to submit expense');
      },
  });
};

export const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: ProjectExpenseUpdate }) =>
      expenseService.updateExpense(id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-expenses', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['expense-stats', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.project_id] });
      toast.success('Expense updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update expense');
      },
  });
};

export const useDeleteExpense = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => expenseService.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-stats'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Expense deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete expense');
      },
  });
};

// Team member hooks
export const useProjectTeamMembers = (projectId: string) => {
  return useQuery({
    queryKey: ['project-team-members', projectId],
    queryFn: () => teamMemberService.fetchProjectTeamMembers(projectId),
    enabled: !!projectId,
  });
};

export const useTeamCosts = (projectId: string) => {
  return useQuery({
    queryKey: ['team-costs', projectId],
    queryFn: () => teamMemberService.calculateTeamCosts(projectId),
    enabled: !!projectId,
  });
};

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (member: ProjectTeamMemberInsert) => teamMemberService.addTeamMember(member),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-team-members', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['team-costs', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.project_id] });
      toast.success('Team member added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add team member');
      },
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: ProjectTeamMemberUpdate }) =>
      teamMemberService.updateTeamMember(id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-team-members', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['team-costs', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.project_id] });
      toast.success('Team member updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update team member');
      },
  });
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => teamMemberService.removeTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-team-members'] });
      queryClient.invalidateQueries({ queryKey: ['team-costs'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Team member removed successfully');
    },
    onError: (error) => {
      toast.error('Failed to remove team member');
      },
  });
};

// Time tracking hooks
export const useProjectTimeEntries = (projectId: string, filters?: any) => {
  return useQuery({
    queryKey: ['project-time-entries', projectId, filters],
    queryFn: () => timeTrackingService.fetchProjectTimeEntries(projectId, filters),
    enabled: !!projectId,
  });
};

export const useUserTimeEntries = (userId: string, filters?: any) => {
  return useQuery({
    queryKey: ['user-time-entries', userId, filters],
    queryFn: () => timeTrackingService.fetchUserTimeEntries(userId, filters),
    enabled: !!userId,
  });
};

export const useTimeStats = (projectId: string, options?: any) => {
  return useQuery({
    queryKey: ['time-stats', projectId, options],
    queryFn: () => timeTrackingService.getTimeStats(projectId, options),
    enabled: !!projectId,
  });
};

export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (entry: ProjectTimeEntryInsert) => timeTrackingService.createTimeEntry(entry),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-time-entries', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['user-time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-stats', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.project_id] });
      toast.success('Time entry created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create time entry');
      },
  });
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: ProjectTimeEntryUpdate }) =>
      timeTrackingService.updateTimeEntry(id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-time-entries', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['user-time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-stats', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.project_id] });
      toast.success('Time entry updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update time entry');
      },
  });
};

export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => timeTrackingService.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['user-time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-stats'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Time entry deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete time entry');
      },
  });
};